import { LegalPage } from "../../components/LegalPage";

export function MedCartInspectionMethodologyPage() {
  return (
    <LegalPage
      title="Med cart inspection methodology"
      eyebrow="Trust center"
      description="How WeConsult Rx structures medication cart and station inspections to close F761 (Drug Storage) + F755 (Pharmacy Services) gaps. References 42 CFR 483.45, USP 797/795/800/659, DEA 21 CFR 1301.75, ISMP, ASHP."
      path="/trust/med-cart-inspection-methodology"
      effective="2026-05-31"
    >
      <p>
        WeConsult Rx provides a structured workflow for medication cart and
        station inspections in skilled nursing (SNF), ambulatory surgery (ASC),
        and assisted living settings. The inspection module is a documentation
        tool to help facility staff complete required compliance rounds. The
        facility&rsquo;s consultant pharmacist and administrator retain full
        regulatory compliance responsibility.
      </p>

      <h2>Regulatory framework</h2>
      <h3>CMS Mega-Rule</h3>
      <ul>
        <li>
          <strong>42 CFR 483.45</strong> — Pharmacy services. Establishes the
          consultant pharmacist role, drug regimen review cadence, and labeling
          / storage standards for nursing facilities.
        </li>
        <li>
          <strong>F-tag F755</strong> — Pharmacy Services / Procedures /
          Pharmacist / Records. Written procedures + records of receipt and
          disposition.
        </li>
        <li>
          <strong>F-tag F756</strong> — Drug Regimen Review. At least monthly
          by a licensed pharmacist; irregularities reported and acted upon.
        </li>
        <li>
          <strong>F-tag F761</strong> — Label / Store Drugs and Biologicals.
          Labeling per accepted standards and secure storage. Core focus of the
          monthly med cart inspection template.
        </li>
      </ul>

      <h3>USP General Chapters</h3>
      <ul>
        <li>
          <strong>USP &lt;659&gt;</strong> — Packaging and storage
          requirements. Drives the refrigerator (36-46&deg;F) and room temp
          (59-86&deg;F) range items in storage sections.
        </li>
        <li>
          <strong>USP &lt;795&gt;</strong> — Pharmaceutical compounding,
          nonsterile preparations. Beyond-use date guardrails for any
          compounded items on cart.
        </li>
        <li>
          <strong>USP &lt;797&gt;</strong> — Pharmaceutical compounding,
          sterile preparations. Drives the 28-day multi-dose vial discard rule
          when no manufacturer guidance is tighter.
        </li>
        <li>
          <strong>USP &lt;800&gt;</strong> — Hazardous drug handling in
          healthcare settings. Cart-side hazardous drug segregation and PPE
          accessibility items.
        </li>
      </ul>

      <h3>DEA controlled substance requirements</h3>
      <ul>
        <li>
          <strong>21 CFR 1301.75</strong> — Physical security controls. Drives
          the &ldquo;Schedule II locked in inner compartment (double-lock)&rdquo;
          item.
        </li>
        <li>
          <strong>21 CFR 1304.04</strong> — Maintenance of records. DEA forms
          (222 / 41) accessibility item.
        </li>
        <li>
          <strong>21 CFR 1304.11</strong> — Inventory requirements. Perpetual
          inventory matches physical count item.
        </li>
        <li>
          <strong>21 CFR 1304.22</strong> — Records for distributors and
          dispensers. Wastage + destruction log items.
        </li>
        <li>
          <strong>21 CFR 1307.21</strong> — Drug destruction. Expired Schedule
          II handling.
        </li>
      </ul>

      <h3>Patient safety guidelines</h3>
      <ul>
        <li>
          <strong>ISMP Best Practices for Hospitals</strong> — Concentrated
          electrolyte (KCl) removal from floor stock; high-alert medication
          segregation.
        </li>
        <li>
          <strong>ISMP High-Alert Medications List</strong> — Drives the
          insulin / anticoagulant / opioid physical separation item.
        </li>
        <li>
          <strong>ISMP &amp; ASHP Look-Alike Sound-Alike (LASA) List</strong> —
          Tall-man lettering and shelf-separation prompts for HYDROmorphone /
          morphine, vinBLAStine / vinCRIStine, and similar pairs.
        </li>
        <li>
          <strong>ASHP Guidelines on Medication-Use Evaluation (2018)</strong>
          {" "}
          — Process framework underlying the structured pass / fail item list.
        </li>
        <li>
          <strong>ASHP Guidelines on Controlled Substances</strong> — Two-witness
          wastage documentation precedent.
        </li>
      </ul>

      <h3>Other</h3>
      <ul>
        <li>
          <strong>OSHA 29 CFR 1910.1030</strong> — Bloodborne pathogens. Sharps
          container fill-line item.
        </li>
        <li>
          <strong>OSHA 29 CFR 1910.132</strong> — Personal protective
          equipment.
        </li>
        <li>
          <strong>SAMHSA Opioid Overdose Prevention Toolkit</strong> — Naloxone
          point-of-use availability.
        </li>
        <li>
          <strong>CDC Vaccine Storage &amp; Handling Toolkit</strong> —
          Refrigerator temp log + excursion items (ASC template).
        </li>
        <li>
          <strong>MHAUS Dantrolene Stock Guidance</strong> — 36-vial dantrolene
          stock item (ASC anesthesia cart).
        </li>
        <li>
          <strong>CMS ASC Conditions for Coverage 42 CFR 416.44 / 416.50</strong>
          {" "}
          — Ambulatory surgery center pharmacy + anesthesia services items.
        </li>
      </ul>

      <h2>How findings are scored</h2>
      <p>
        Each item carries a <code>severity_if_fail</code> classification
        seeded with the template:
      </p>
      <ul>
        <li>
          <strong>Critical</strong> — Immediate-jeopardy equivalent. Locked
          storage, controlled substance count match, expired emergency drugs,
          mislabeled insulin pens. Drives an automatic 14-day corrective
          action window when found on submit.
        </li>
        <li>
          <strong>Major</strong> — Actual-harm potential. Beyond-use dates,
          multi-dose vial labeling, AED expiry.
        </li>
        <li>
          <strong>Moderate</strong> — Pattern indicating a process gap.
          Cleanliness, debris.
        </li>
        <li>
          <strong>Minor</strong> — Potential-for-harm with no current impact.
          Hand sanitizer expiry, PPE accessibility.
        </li>
      </ul>

      <h2>PHI safeguards</h2>
      <p>
        Finding free-text MAY incidentally reference a resident name if a
        resident-specific Rx is found in the wrong location (for example, an
        insulin pen labeled for resident X discovered in another wing&rsquo;s
        cart). Per <strong>HIPAA &sect; 164.502(b)</strong> minimum necessary:
      </p>
      <ul>
        <li>
          Findings JSONB is scoped to the tenant via Postgres Row-Level
          Security (RLS).
        </li>
        <li>
          Photo evidence uploaded to the <code>med-cart-evidence</code>{" "}
          Supabase storage bucket lives at{" "}
          <code>t/&lt;tenant_id&gt;/&lt;inspection_id&gt;/&lt;photo_id&gt;.jpg</code>{" "}
          with a tenant-prefixed RLS SELECT policy.
        </li>
        <li>
          Audit log entries (<code>mcinspect.started</code>,{" "}
          <code>mcinspect.submitted</code>,{" "}
          <code>mcinspect.corrective_completed</code>) carry only{" "}
          <code>facility_id</code>, <code>inspection_id</code>,{" "}
          <code>finding_count</code>, <code>critical_finding_count</code>, and
          a severity distribution map. Finding text and photo URLs are{" "}
          <em>never</em> written to the audit log.
        </li>
      </ul>

      <h2>Inspector identity</h2>
      <p>
        The inspector role is recorded as one of: registered pharmacist (RPh),
        registered nurse (RN), director of nursing (DON), consultant
        pharmacist, administrator, or privacy officer. Inspector name is
        optional and is stored in the inspection row but never in the audit
        log, since the actor user id is already captured by the audit
        infrastructure.
      </p>

      <h2>Disclaimer</h2>
      <p>
        WeConsult Rx provides this inspection workflow as a documentation tool
        to help facility staff complete compliance rounds. The
        facility&rsquo;s consultant pharmacist and administrator retain
        regulatory compliance responsibility. Use of this module does not
        constitute pharmacy practice on behalf of WeConsult Rx and does not
        replace the consultant pharmacist&rsquo;s monthly drug regimen review
        per <strong>F756</strong>.
      </p>
    </LegalPage>
  );
}
